/**
 * Image Service
 * Service for fetching images from the thumbnail/image API
 * Follows SOLID principles:
 * - Single Responsibility: Handles image fetching only
 * - Open/Closed: Can be extended with new image sources without modifying existing code
 * - Dependency Inversion: Uses abstraction (IImageProvider) instead of concrete implementation
 */

import axios from 'axios';

/**
 * Image Provider Interface
 * Defines contract for image providers (follows Interface Segregation Principle)
 */
export interface IImageProvider {
  fetchImage(
    query: string,
    options?: { description?: string; placeName?: string; category?: string }
  ): Promise<string | null>;
}

/**
 * Thumbnail Image Provider
 * Implements IImageProvider for the thumbnail/image API
 * Uses POST requests to /api/thumbnail/image with title, description, and placeId
 */
export class ThumbnailImageProvider implements IImageProvider {
  private baseUrl: string;
  // In-memory cache for request deduplication - prevents duplicate requests
  private static inFlightRequests = new Map<string, Promise<string | null>>();
  // Cache for successful responses
  private static responseCache = new Map<string, string | null>();

  constructor(baseUrl?: string) {
    this.baseUrl =
      baseUrl ||
      process.env.NEXT_PUBLIC_API_URL ||
      'http://localhost:4000/nexgo-gateway';
  }

  /**
   * Clear the image cache - useful when switching locations
   */
  static clearCache(): void {
    console.log('🧹 [ThumbnailImageProvider] Clearing image cache:', {
      cacheSize: ThumbnailImageProvider.responseCache.size,
      inFlightRequests: ThumbnailImageProvider.inFlightRequests.size,
    });
    ThumbnailImageProvider.responseCache.clear();
    ThumbnailImageProvider.inFlightRequests.clear();
  }

  /**
   * Clear cache for a specific place - useful when updating location data
   */
  static clearCacheForPlace(placeName: string): void {
    const keysToDelete: string[] = [];
    for (const [key] of ThumbnailImageProvider.responseCache) {
      if (key.includes(`|${placeName.toLowerCase()}|`)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => {
      ThumbnailImageProvider.responseCache.delete(key);
    });

    console.log('🧹 [ThumbnailImageProvider] Cleared cache for place:', {
      placeName,
      keysDeleted: keysToDelete.length,
      remainingCacheSize: ThumbnailImageProvider.responseCache.size,
    });
  }

  /**
   * Generate a cache key from request parameters
   * Includes placeName to ensure different locations get different images
   */
  private getCacheKey(
    query: string,
    options?: { description?: string; placeName?: string; category?: string }
  ): string {
    const parts = [
      query.trim().toLowerCase(),
      options?.description?.trim().toLowerCase() || '',
      options?.placeName?.trim().toLowerCase() || '', // Critical: include placeName to differentiate locations
      options?.category?.trim().toLowerCase() || '',
    ];
    return parts.join('|');
  }

  /**
   * Fetch image from Thumbnail API
   * Makes a POST request to /api/thumbnail/image
   * Uses request deduplication to prevent duplicate requests
   * Requests are NEVER cancelled - they complete regardless of component lifecycle
   * @param query - Search query for the image (used as title in request body)
   * @param options - Optional parameters (description, placeName, category)
   * @returns Promise resolving to image URL or null if not found
   */
  async fetchImage(
    query: string,
    options?: { description?: string; placeName?: string; category?: string }
  ): Promise<string | null> {
    if (!query || !query.trim()) {
      console.log('🔍 [ThumbnailImageProvider] Skipping - empty query');
      return null;
    }

    const cacheKey = this.getCacheKey(query, options);

    console.log('🔑 [ThumbnailImageProvider] Generated cache key:', {
      query: query.trim(),
      placeName: options?.placeName,
      category: options?.category,
      cacheKey,
      cacheSize: ThumbnailImageProvider.responseCache.size,
      inFlightRequests: ThumbnailImageProvider.inFlightRequests.size,
    });

    // Check if we have a cached response
    if (ThumbnailImageProvider.responseCache.has(cacheKey)) {
      const cachedResult = ThumbnailImageProvider.responseCache.get(cacheKey);
      console.log('💾 [ThumbnailImageProvider] Using cached response:', {
        query: query.trim(),
        placeName: options?.placeName,
        hasResult: cachedResult !== null,
        cacheKey,
      });
      return cachedResult;
    }

    // Check if request is already in flight (deduplication)
    if (ThumbnailImageProvider.inFlightRequests.has(cacheKey)) {
      console.log(
        '🔄 [ThumbnailImageProvider] Request deduplication: Reusing in-flight request:',
        {
          query: query.trim(),
          inFlightCount: ThumbnailImageProvider.inFlightRequests.size,
        }
      );
      // Return the existing promise - request will complete regardless of component lifecycle
      return ThumbnailImageProvider.inFlightRequests.get(cacheKey)!;
    }

    const requestBody = {
      title: query.trim(),
      ...(options?.description ? { description: options.description } : {}),
      ...(options?.placeName ? { placeName: options.placeName } : {}),
      ...(options?.category ? { category: options.category } : {}),
    };

    // Create the request promise
    const requestPromise = (async (): Promise<string | null> => {
      try {
        // Use Next.js API route to avoid CORS and keep API logic centralized
        const apiUrl = '/api/thumbnail/image';

        console.log('📤 [ThumbnailImageProvider] Sending NEW request to:', {
          apiUrl,
          requestBody,
          cacheKey,
        });

        // Always use POST request
        // CRITICAL: No AbortSignal is passed - request will NEVER be cancelled
        const response = await axios.post(apiUrl, requestBody, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 30000, // Increased timeout to 30 seconds to prevent premature cancellation
        });

        console.log('📥 [ThumbnailImageProvider] Response received:', {
          status: response.status,
          statusText: response.statusText,
          dataKeys: Object.keys(response.data || {}),
          hasUrl: !!(
            response.data?.url ||
            response.data?.data?.url ||
            response.data?.data?.response?.url
          ),
        });

        // Extract image URL from response
        const imageUrl =
          response.data?.data?.response?.url ||
          response.data?.data?.url ||
          response.data?.response?.url ||
          response.data?.url ||
          null;

        // Enhanced logging for debugging
        console.log('🖼️ [ThumbnailImageProvider] Image URL received:', {
          query: requestBody.title,
          category: requestBody.category,
          placeName: requestBody.placeName,
          imageUrl: imageUrl,
          imageUrlPreview: imageUrl
            ? imageUrl.substring(0, 100) + (imageUrl.length > 100 ? '...' : '')
            : null,
          imageUrlDomain: imageUrl ? new URL(imageUrl).hostname : null,
          cacheKey,
        });

        if (!imageUrl) {
          console.warn(
            '⚠️ [ThumbnailImageProvider] No image URL in response:',
            {
              requestBody,
              responseData: response.data,
            }
          );
        } else {
          console.log(
            '✅ [ThumbnailImageProvider] Successfully fetched image:',
            {
              query: requestBody.title,
              imageUrl:
                imageUrl.substring(0, 100) +
                (imageUrl.length > 100 ? '...' : ''),
            }
          );
        }

        // Cache the result (even if null, to avoid repeated failed requests)
        ThumbnailImageProvider.responseCache.set(cacheKey, imageUrl);

        console.log('💾 [ThumbnailImageProvider] Cached image result:', {
          query: requestBody.title,
          placeName: requestBody.placeName,
          category: requestBody.category,
          hasImage: !!imageUrl,
          imageUrlPreview: imageUrl ? imageUrl.substring(0, 50) + '...' : null,
          cacheKey,
          totalCacheSize: ThumbnailImageProvider.responseCache.size,
        });

        return imageUrl;
      } catch (error) {
        // 404 (not found) and 422 (unprocessable entity) are expected when no images are available
        // 422 typically means the request was valid but no images were found
        if (
          axios.isAxiosError(error) &&
          (error.response?.status === 404 || error.response?.status === 422)
        ) {
          console.warn('⚠️ [ThumbnailImageProvider] Image not found:', {
            requestBody,
            status: error.response.status,
            statusText: error.response.statusText,
          });
          // Cache null result to avoid repeated failed requests
          ThumbnailImageProvider.responseCache.set(cacheKey, null);
          return null;
        }

        // Check if request was cancelled (should not happen, but handle gracefully)
        if (
          axios.isAxiosError(error) &&
          (error.code === 'ERR_CANCELED' || error.message.includes('canceled'))
        ) {
          console.error(
            '❌ [ThumbnailImageProvider] Request was cancelled (unexpected):',
            {
              message: error.message,
              code: error.code,
              requestBody,
            }
          );
          // Don't cache cancelled requests - allow retry
          throw error; // Re-throw to allow retry
        }

        // Log detailed error information
        if (axios.isAxiosError(error)) {
          console.error('❌ [ThumbnailImageProvider] API Error:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            requestBody,
            url: error.config?.url,
            method: error.config?.method,
          });
        } else {
          console.error('❌ [ThumbnailImageProvider] Unexpected error:', {
            error,
            requestBody,
          });
        }
        // Cache null for errors to prevent repeated failed requests
        ThumbnailImageProvider.responseCache.set(cacheKey, null);
        return null;
      } finally {
        // Remove from in-flight requests when done (success or failure)
        ThumbnailImageProvider.inFlightRequests.delete(cacheKey);
        console.log(
          '✅ [ThumbnailImageProvider] Request completed, removed from in-flight:',
          {
            cacheKey,
            remainingInFlight: ThumbnailImageProvider.inFlightRequests.size,
          }
        );
      }
    })();

    // Store the promise in in-flight requests for deduplication
    ThumbnailImageProvider.inFlightRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }
}

/**
 * Image Service
 * Main service for fetching images (follows Dependency Inversion Principle)
 */
export class ImageService {
  private provider: IImageProvider;

  constructor(provider?: IImageProvider) {
    // Default to Thumbnail provider if none provided
    this.provider = provider || new ThumbnailImageProvider();
  }

  /**
   * Fetch image for a given query
   * @param query - Search query for the image
   * @param options - Optional parameters (description, placeName, category)
   * @returns Promise resolving to image URL or null
   */
  async fetchImage(
    query: string,
    options?: { description?: string; placeName?: string; category?: string }
  ): Promise<string | null> {
    return this.provider.fetchImage(query, options);
  }

  /**
   * Fetch images for multiple queries in parallel
   * Uses Promise.allSettled to ensure all requests complete even if some fail
   * @param queries - Array of search queries
   * @returns Promise resolving to array of image URLs (null for failed requests)
   */
  async fetchImages(queries: string[]): Promise<(string | null)[]> {
    const promises = queries.map(query => this.fetchImage(query));

    // Use Promise.allSettled instead of Promise.all to prevent one failure from failing all
    const results = await Promise.allSettled(promises);

    // Map results: fulfilled values are returned, rejected are converted to null
    return results.map(result => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        console.warn(
          '⚠️ [ImageService] One image fetch failed (continuing with others):',
          {
            reason: result.reason,
          }
        );
        return null;
      }
    });
  }
}

// Export singleton instance for convenience
export const imageService = new ImageService();
